import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { OptionNameValuePair } from "../../../../../src/lib/interfaces/types";
import { cn } from "../../../../../src/lib/utils";
import { Search } from "lucide-react";


interface TooltipSelectProps<T> {
  value?: T;
  onSelectionChange?: (value: T) => void;
  options?: OptionNameValuePair<T>[];
  label?: string;
  disabled?: boolean;
  rootContainerClassName?: string;
  triggerClassName?: string;
  dropdownClassName?: string;
  optionClassName?: string;
  maxHeight?: number;
  enableSearch?: boolean; 
}

function TooltipSelect<T>({
  value,
  onSelectionChange,
  options = [],
  label = "+ Add",
  disabled = false,
  rootContainerClassName,
  triggerClassName,
  dropdownClassName,
  optionClassName,
  maxHeight = 240,
  enableSearch=false, 
}: TooltipSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties | null>(null);
  const [arrowDirection, setArrowDirection] = useState<"top" | "bottom">(
    "top"
  );
  const [searchQuery, setSearchQuery] = useState("");

  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);


  const filteredOptions = enableSearch
  ? options.filter((option) =>
      option.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    )
  : options;

  const updatePosition = () => {
    const anchor = triggerRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const estimatedHeight = maxHeight;

    const spaceBelow = window.innerHeight - rect.bottom;
    const shouldFlip = spaceBelow < estimatedHeight;

    const left = rect.left + rect.width / 2;

    const baseStyle: React.CSSProperties = {
      position: "fixed",
      left,
      transform: "translateX(-50%)",
      zIndex: 9999,
    };

    if (shouldFlip) {
      baseStyle.top = rect.top - 8;
      baseStyle.transform += " translateY(-100%)";
      setArrowDirection("bottom");
    } else {
      baseStyle.top = rect.bottom + 8;
      setArrowDirection("top");
    }

    setStyle(baseStyle);
  };

  useLayoutEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleScroll = () => updatePosition();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className={cn("inline-block", rootContainerClassName)}>
      <div
        ref={triggerRef}
        onClick={() => {
          if (disabled) return;
          setIsOpen((prev) => !prev);
        }}
        className={cn(
          `
          px-2 py-0.5
          rounded-[8px]
          bg-[#F1F5F9]
          border border-transparent
          text-xs
          font-normal
          text-[#0A0A0A]
          leading-4
          cursor-pointer
          `,
          disabled && "opacity-50 cursor-not-allowed",
          triggerClassName
        )}
      >
        {label || selectedOption?.name }
      </div>

      {isOpen &&
        style &&
        ReactDOM.createPortal(
          <div ref={dropdownRef} style={style}>
            <div
              className={cn(
                "relative bg-white border border-gray-300 rounded-md shadow-overviewCard w-[220px] py-2.5 px-1",
                dropdownClassName
              )}
            >
              <div
                className={cn(
                  "absolute left-1/2 -translate-x-1/2 w-3 h-3 bg-white border-l border-t border-gray-300 rotate-45 pointer-events-none",
                  arrowDirection === "top" ? "-top-[7px]" : "-bottom-[7px] rotate-[225deg]"
                )}
              />

              <div
                className="overflow-auto"
                style={{ maxHeight }}
              >
                {enableSearch && (
                  <div className="sticky top-0 bg-white p-1">
                    <div className="relative">
                      <Search className="absolute left-2 top-1.5 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search"
                        className="w-full pl-7 pr-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                )}
                {filteredOptions.length >0 && (filteredOptions.map((option) => {
                  const isSelected = option.value === value;
                  return (
                    <div
                      key={String(option.value)}
                      className={cn(
                        "px-4 py-2 text-sm cursor-pointer rounded-md hover:bg-gray-100",
                        isSelected && "bg-gray-100 font-medium",
                        optionClassName
                      )}
                      onClick={() => {
                        onSelectionChange?.(option.value);
                        setIsOpen(false);
                        setSearchQuery("");
                      }}
                    >
                      {option.name}
                    </div>
                  );
                }) )}
                {enableSearch && searchQuery.trim().length > 0  && filteredOptions.length === 0 && (
                  <div className="px-4 py-2 text-sm text-gray-400">
                    No results found
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default TooltipSelect;
